'use client'

import { useState, useEffect } from 'react'
import { AlertCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import { useQuery } from '@tanstack/react-query'

interface Alert {
  id: string
  patientId: string
  room: string
  name: string
  message: string
  severity: 'critical' | 'warning' | 'info'
  timestamp: Date
  isRead: boolean
}

export function AlertBanner() {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0)
  const [dismissedAlerts, setDismissedAlerts] = useState<Map<string, number>>(new Map()) // Track dismissal time
  const [firstAlertTime, setFirstAlertTime] = useState<number | null>(null)
  const [soundEnabled, setSoundEnabled] = useState(true)

  // 실제 알림 데이터 가져오기 - 여러 병동에서 수집 (실시간 모니터링과 연동)
  const { data: riskData } = useQuery({
    queryKey: ['all-patients-realtime'], // 실시간 대시보드와 공유
    queryFn: async () => {
      const wards = ['3A', '3B', '4A', '4B', '5A', '5B', '6A', '6B', '7A', '7B', '8A', '8B', '9ICU', '10VIP']
      const allPredictions = []

      for (const ward of wards) {
        try {
          const response = await fetch(`/api/v1/inference/risk?ward_id=${ward}`)
          const data = await response.json()
          if (data.predictions && data.predictions.length > 0) {
            allPredictions.push(...data.predictions)
          }
        } catch (error) {
          console.error(`Failed to fetch data for ward ${ward}:`, error)
        }
      }

      return {
        predictions: allPredictions,
        totalPatients: allPredictions.length
      }
    },
    refetchInterval: 30000 // 30초마다 업데이트 (실시간 모니터링과 동일)
  })

  useEffect(() => {
    const patients = Array.isArray(riskData) ? riskData : riskData?.predictions || []
    console.log('AlertBanner: Fetched patients:', patients.length, 'patients')
    if (patients && patients.length > 0) {
      // Log all risk scores for debugging
      console.log('Risk scores:', patients.map((p: any) => ({ id: p.patientId, risk: p.riskScore })))

      const highRiskPatients = patients
        .filter((p: any) => p.riskScore >= 0.85) // More strict criteria - only very high risk
        .sort((a: any, b: any) => b.riskScore - a.riskScore) // Sort by risk score descending
        .slice(0, 3) // Take only top 3 highest risk patients
        .map((p: any) => ({
          id: `alert-${p.patientId}`, // Consistent ID without timestamp
          patientId: p.patientId,
          room: p.room,
          name: p.name,
          message: p.riskScore >= 0.95 ? '호흡 정지 위험' :
                   p.riskScore >= 0.90 ? '호흡 억제 고위험' :
                   '응급 모니터링 필요',
          severity: (p.riskScore >= 0.90 ? 'critical' : 'warning') as 'critical' | 'warning',
          timestamp: new Date(),
          isRead: false
        }))
        .filter((alert: Alert) => {
          const dismissedTime = dismissedAlerts.get(alert.id)
          if (!dismissedTime) return true

          // 5분 후 다시 표시 (300000ms)
          const fiveMinutes = 5 * 60 * 1000
          return Date.now() - dismissedTime > fiveMinutes
        })

      // Replace all alerts with current high-risk ones (don't accumulate)
      if (highRiskPatients.length > 0) {
        // 처음 20초 후 첫 알람, 그 후 30초마다 업데이트
        if (!firstAlertTime) {
          setFirstAlertTime(Date.now())
          // 20초 후 첫 알람 표시 (테스트용)
          console.log('First alert will appear in 20 seconds...')
          setTimeout(() => {
            console.log('Showing alerts now!')
            setAlerts(highRiskPatients)
            playAlertSound()
          }, 20000) // 20초
        } else {
          // 이미 첫 알람이 지난 경우, 30초마다만 업데이트
          const timeSinceFirst = Date.now() - firstAlertTime
          if (timeSinceFirst > 20000) {
            setAlerts(highRiskPatients)
          }
        }
      } else {
        // Clear alerts if no high-risk patients
        setAlerts([])
      }
    }
  }, [riskData, dismissedAlerts])

  // 알림 사운드 재생 함수
  const playAlertSound = () => {
    console.log('playAlertSound called')

    // localStorage에서 사운드 설정 확인
    const soundSetting = localStorage.getItem('soundEnabled')
    console.log('localStorage soundEnabled:', soundSetting)

    if (soundSetting === 'false') {
      console.log('Sound is disabled, not playing alert')
      return
    }

    console.log('Attempting to play alert sound...')
    try {
      const audio = new Audio('/sounds/alert.mp3')
      audio.volume = 0.6

      // 무조건 재생 시도 (테스트용)
      audio.play()
        .then(() => {
          console.log('Alert sound played successfully!')
        })
        .catch(err => {
          console.log('Alert sound failed:', err)
          // 사용자가 페이지와 상호작용하지 않은 경우
          if (err.name === 'NotAllowedError') {
            console.log('User needs to interact with page first. Click anywhere on the page.')
            // 사용자 상호작용 유도를 위한 이벤트 리스너 추가
            const playOnClick = () => {
              audio.play().then(() => {
                console.log('Alert sound played after user interaction!')
                document.removeEventListener('click', playOnClick)
              }).catch(e => console.error('Still failed:', e))
            }
            document.addEventListener('click', playOnClick)
          }
        })
    } catch (error) {
      console.log('Could not play alert sound:', error)
    }
  }

  // 사운드 설정 초기화 및 storage 이벤트 리스닝
  useEffect(() => {
    const savedSoundSetting = localStorage.getItem('soundEnabled')
    setSoundEnabled(savedSoundSetting !== 'false')

    // 사용자 상호작용 감지
    const handleUserInteraction = () => {
      localStorage.setItem('userInteracted', 'true')
      console.log('User interaction detected - sound enabled')

      // 이벤트 리스너 제거
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
    }

    // 처음 클릭이나 키 입력 시 상호작용 플래그 설정
    if (localStorage.getItem('userInteracted') !== 'true') {
      document.addEventListener('click', handleUserInteraction)
      document.addEventListener('keydown', handleUserInteraction)
    }

    // storage 이벤트 리스너 추가
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'soundEnabled') {
        setSoundEnabled(e.newValue !== 'false')
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('click', handleUserInteraction)
      document.removeEventListener('keydown', handleUserInteraction)
    }
  }, [])

  const activeAlerts = alerts

  // Auto-cycle through alerts every 10 seconds
  useEffect(() => {
    if (activeAlerts.length > 1) {
      const interval = setInterval(() => {
        setCurrentAlertIndex((prev) => (prev + 1) % activeAlerts.length)
      }, 30000) // Change alert every 30 seconds

      return () => clearInterval(interval)
    }
  }, [activeAlerts.length])

  const handleDismiss = (alertId: string) => {
    // Record dismissal time
    setDismissedAlerts(prev => new Map(prev).set(alertId, Date.now()))

    // Remove from current alerts
    setAlerts(prev => {
      const filtered = prev.filter(a => a.id !== alertId)

      // Adjust current index if needed
      if (currentAlertIndex >= filtered.length && filtered.length > 0) {
        setCurrentAlertIndex(Math.max(0, filtered.length - 1))
      } else if (filtered.length === 0) {
        setCurrentAlertIndex(0)
      }

      return filtered
    })
  }
  const currentAlert = activeAlerts[currentAlertIndex]

  // Show "All Green" when no alerts
  if (activeAlerts.length === 0) {
    return (
      <div className="w-full bg-green-600 text-white p-3">
        <div className="container mx-auto flex items-center justify-center">
          <div className="flex items-center gap-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22,4 12,14.01 9,11.01"></polyline>
            </svg>
            <span className="font-medium">All Green - No alerts at this time!</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`w-full ${currentAlert?.severity === 'critical' ? 'bg-destructive' : 'bg-orange-500'} text-white p-3`}>
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-5 w-5 animate-pulse" />
          <span className="font-medium">
            환자 #{currentAlert?.patientId} (병실 {currentAlert?.room}) - {' '}
            <Link
              href={`/patients/${currentAlert?.patientId}`}
              className="hover:underline text-white underline"
            >
              {currentAlert?.name}
            </Link>
            {' - '}{currentAlert?.message}
          </span>
          {activeAlerts.length > 0 && (
            <Badge className="bg-white/20 text-white">
              알림 {activeAlerts.length}개
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDismiss(currentAlert?.id || '')}
              className="text-white hover:bg-white/20"
            >
              <X className="h-4 w-4" />
            </Button>

            {/* Hover tooltip */}
            <div className="absolute top-full right-0 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              (확인)
              <div className="absolute bottom-full right-2 w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-gray-800"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Badge({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${className}`}>
      {children}
    </span>
  )
}